import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Fade,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const SearchBar = ({ onLocationSelect, onFocus, onBlur }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const shellBg = isDark
    ? alpha(theme.palette.background.paper, 0.85)
    : theme.palette.common.white;
  const idleShadow = isDark
    ? `0 6px 18px ${alpha(theme.palette.common.black, 0.75)}`
    : `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`;
  const focusShadow = isDark
    ? `0 16px 42px ${alpha(theme.palette.common.black, 0.85)}`
    : `0 12px 30px ${alpha(theme.palette.common.black, 0.18)}`;
  const inputBg = isDark
    ? alpha(theme.palette.background.paper, 0.95)
    : theme.palette.common.white;
  const borderColor = isDark
    ? alpha(theme.palette.common.white, 0.2)
    : theme.palette.grey[300];
  const dropdownBg = isDark
    ? alpha(theme.palette.background.paper, 0.98)
    : theme.palette.common.white;
  const dropdownBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.08)}`;

  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const searchTimeout = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const waitForGoogle = setInterval(() => {
      if (window.google?.maps?.places) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
        sessionToken.current =
          new window.google.maps.places.AutocompleteSessionToken();
        clearInterval(waitForGoogle);
      }
    }, 200);

    return () => {
      clearInterval(waitForGoogle);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearchInput = async (value) => {
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!value.trim()) {
      setPredictions([]);
      setIsLoading(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsLoading(true);
      if (!autocompleteService.current) {
        setIsLoading(false);
        return;
      }

      try {
        const request = {
          input: value,
          componentRestrictions: { country: "IN" },
          sessionToken: sessionToken.current,
        };

        const response = await new Promise((resolve, reject) => {
          autocompleteService.current.getPlacePredictions(
            request,
            (results, status) => {
              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                results
              ) {
                resolve(results);
              } else {
                reject(status);
              }
            }
          );
        });

        setPredictions(response || []);
      } catch (error) {
        setPredictions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const handlePlaceSelect = async (prediction) => {
    setSearchQuery(prediction.description);
    setPredictions([]);
    setIsLoading(true);

    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { placeId: prediction.place_id },
          (results, status) => {
            if (status === "OK" && results?.[0]) {
              resolve(results[0]);
            } else {
              reject(new Error(status));
            }
          }
        );
      });

      const location = {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng(),
        address: prediction.description,
        placeId: prediction.place_id,
      };

      if (onLocationSelect) {
        onLocationSelect(location);
      }

      sessionToken.current =
        new window.google.maps.places.AutocompleteSessionToken();
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsFocused(false);
    }
  };

  const handleClear = () => {
    setSearchQuery("");
    setPredictions([]);
    sessionToken.current =
      new window.google.maps.places.AutocompleteSessionToken();
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      if (onBlur) onBlur();
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsFocused(false);
        setPredictions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{ position: "relative", width: "100%", maxWidth: 600 }}
    >
      <motion.div
        initial={false}
        animate={{
          scale: isFocused ? 1.02 : 1,
          boxShadow: isFocused ? focusShadow : idleShadow,
        }}
        transition={{ duration: 0.2 }}
        style={{
          borderRadius: "9999px",
          overflow: "hidden",
          backgroundColor: shellBg,
          display: "inline-block",
          width: "100%",
          border: `1px solid ${borderColor}`,
        }}
      >
        <TextField
          fullWidth
          value={searchQuery}
          onChange={(e) => handleSearchInput(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Search for a location or charging station..."
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "9999px !important",
              overflow: "hidden",
              "& fieldset": {
                borderColor: isFocused
                  ? theme.palette.primary.main
                  : borderColor,
                borderWidth: isFocused ? 2 : 1,
                borderRadius: "9999px !important",
              },
              "&:hover fieldset": {
                borderColor: isFocused
                  ? theme.palette.primary.main
                  : alpha(borderColor, 1),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              },
              backgroundColor: inputBg,
            },
            "& .MuiInputBase-input": {
              padding: "14px 18px",
              borderRadius: "9999px !important",
              color: theme.palette.text.primary,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationIcon
                  sx={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : searchQuery ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon sx={{ color: theme.palette.text.secondary }} />
                  </IconButton>
                ) : (
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      </motion.div>

      <AnimatePresence>
        {predictions.length > 0 && (
          <Fade in={predictions.length > 0}>
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: 1,
                maxHeight: 300,
                overflow: "auto",
                zIndex: 1000,
                borderRadius: 2,
                boxShadow: isDark
                  ? `0 18px 40px ${alpha(theme.palette.common.black, 0.7)}`
                  : 3,
                backgroundColor: dropdownBg,
                border: dropdownBorder,
              }}
            >
              <List disablePadding>
                {predictions.map((prediction) => (
                  <ListItem key={prediction.place_id} disablePadding>
                    <ListItemButton
                      onClick={() => handlePlaceSelect(prediction)}
                      sx={{
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.12
                          ),
                        },
                      }}
                    >
                      <LocationIcon
                        sx={{
                          mr: 2,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <ListItemText
                        primary={prediction.structured_formatting.main_text}
                        secondary={
                          prediction.structured_formatting.secondary_text
                        }
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{
                          color: "text.secondary",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Fade>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default SearchBar;
